import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { CallController } from '../../components/index';

import Main from '../../components/VideoCall/main';

import socket from '../../socket';

import { alertError, alertSuccess } from '../../redux/stateActions';

import { GET_SESSION_STATUS } from '../../services/patient';

const Call = ({ history }) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const user = useSelector((state) => state.user);

    /*--------------- Call Control Controller ---------------*/
    const roomInitRef = useRef();

    const [patientSummary, setPatientSummary] = useState({});

    useEffect(() => {
        if (!id) {
            history.replace('/home');
        }
    }, [id]);

    /*--------------- Get Session ---------------*/
    const { data: getSessionData, error: getSessionError } = useQuery(GET_SESSION_STATUS, {
        variables: {
            id,
        },
        skip: _.isEmpty(user) || !id,
        pollInterval: 300000,
    });

    useEffect(() => {
        if (getSessionError) {
            dispatch(alertError(`Cannot Get Session: ${getSessionError.message}`));
        }

        if (getSessionData && !_.isEmpty(getSessionData.session)) {
            const { actualEndTime, sessionEnd, cancelled, patient_summaries } =
                getSessionData.session;

            // check if session cancelled
            if (cancelled) {
                dispatch(alertError(`Session Cancelled`));
                history.replace('/provider/appointment');
                return;
            }

            // check if session end
            if (sessionEnd) {
                dispatch(alertSuccess(`Session Ended`));
                history.replace('/provider/appointment');
                return;
            }

            // check if video call end
            if (actualEndTime) {
                dispatch(alertSuccess(`Session Ended`));
                history.replace(`/appointment/one/${id}`);
                return;
            }

            const foundPatientSummary = patient_summaries.find(
                (s) => s.patient.id === user.patient.id
            );

            if (!foundPatientSummary) {
                dispatch(alertError(`unauthorized session`));
                history.replace(`/appointment/one/${id}`);
            }

            setPatientSummary(foundPatientSummary);
        }
    }, [getSessionError, getSessionData]);

    /*--------------- End Call ---------------*/
    const endCallCallback = () => {
        socket.emit('patientEndCall', {
            patientId: patientSummary.patient.id,
            patientSummaryId: patientSummary.id,
            sessionId: id,
            staffId: getSessionData.session.meet.staff.id,
        });
        return history.replace(`/appointment/one/${id}?status=NOTE`);
    };

    useEffect(() => {
        if (!_.isEmpty(patientSummary)) {
            // update join time
            socket.emit('patientJoinOneCall', {
                sessionId: id,
                patientSummaryId: patientSummary.id,
                patientId: patientSummary.patient.id,
            });

            // fire when reconnect after disconnect
            socket.on('connect', () => {
                socket.emit('reconnectOneCall', {
                    sessionId: id,
                    patientSummaryId: patientSummary.id,
                    patientId: patientSummary.patient.id,
                });
            });

            socket.on('providerEndOneCall', (data) => {
                if (data.sessionId === id) {
                    endCallCallback();
                }
            });
        }
    }, [patientSummary]);

    useEffect(() => {
        // provider turn off mic
        socket.on('providerTurnPatientMicOff', (data) => {
            if (data.sessionId === id) {
                roomInitRef.current.trunClientMicOff();
            }
        });

        // provider turn off camera
        socket.on('providerTurnPatientCameraOff', (data) => {
            if (data.sessionId === id) {
                roomInitRef.current.turnClientWebcamOff();
            }
        });
    }, [roomInitRef]);


    return (
        <div style={{ height: '100vh', width: '100vw' }}>
            <Main
                ref={roomInitRef}
                type="one"
                isProvider={false}
                faceDetection={false}
                patientId={patientSummary.patient?.id}
                useMicCamera={true}
            />
            <CallController
                isProvider={false}
                handleMic={() => roomInitRef.current.micControl()}
                handleCamera={() => roomInitRef.current.webcamControl()}
                onEndCall={() => {
                    try {
                        roomInitRef.current.clientLeaveRoom(endCallCallback);
                    } catch (error) {
                        endCallCallback();
                    }
                }}
            />
        </div>
    );
};

Call.propTypes = {
    history: PropTypes.object,
};

export default Call;
